import { Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { IconCheck, IconCopy } from '@tabler/icons-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { hashMessage, isAddress } from 'viem'
import { useSignMessage } from 'wagmi'

import airdropScreenshot from '@/assets/webp_converted/airdropScreenshot.webp'
import NeedHelp from '@/components/Airdrop/NeedHelp'
import { AirdropRecipient, DISCORD_AIRDROP } from '@/components/Airdrop/utils'
import { callbackCopyMessage } from '@/components/Buttons/CopyButton'
import RootButton from '@/components/Buttons/RootButton'
import ErrorMessage from '@/components/ErrorMessage'
import Loading from '@/components/Loading'
import SpecialInputTile from '@/components/SpecialInputTile'
import useCopyClipboard from '@/hooks/useCopyToClipboard'
import useSessionStorage from '@/hooks/useSessionStorage'
import { useAuth } from '@/plugins/auth'

import classes from '../../Transfer/Send.module.css'

const ConfirmedChange: React.FC<{
  setEnteredDiscord: (value: boolean) => void
}> = ({ setEnteredDiscord }) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const { disconnect } = useAuth()
  return (
    <Stack style={{ gap: '20px' }}>
      <Text style={{ color: theme.colors.title[0], fontSize: '18px' }}>
        {t(
          'components.airdrop.modal.confirmation',
          'Once you’ve received this confirmation from the Airdrop bot, log out of the LAMINA1 Hub and log in with the address you transferred your rewards to. Then complete the claim process by going to the Airdrop tab on that account.'
        )}
      </Text>
      <img alt="discord-screenshot" src={airdropScreenshot} style={{ borderRadius: '12px' }} />
      <Stack style={{ flexDirection: 'row' }}>
        <RootButton secondary style1={false} expand onClick={() => setEnteredDiscord(false)}>
          {t('buttons.back', 'Back')}
        </RootButton>
        <RootButton expand style1={false} onClick={disconnect}>
          {t('buttons.logout', 'Logout')}
        </RootButton>
      </Stack>
      <NeedHelp bot assistance />
    </Stack>
  )
}

interface SignedChangeAddressProps {
  setEnteredDiscord: (value: boolean) => void
  discordMessage: string
}

const SignedChangeAddress: React.FC<SignedChangeAddressProps> = ({
  setEnteredDiscord,
  discordMessage,
}) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const [isCopied, staticCopy] = useCopyClipboard()
  const [enableGoToDiscord, setEnableGoToDiscord] = useState<boolean>(false)

  useEffect(() => {
    if (isCopied) {
      setEnableGoToDiscord(true)
    }
  }, [isCopied])

  const handleGoToDiscord = useCallback(() => {
    setEnteredDiscord(true)
    window.open(DISCORD_AIRDROP, '_blank')
  }, [])
  return (
    <Stack style={{ gap: '20px' }}>
      <Text style={{ color: theme.colors.title[0], fontSize: '18px' }}>
        <Trans i18nKey="components.airdrop.modal.signRequest">
          Here’s your signed transfer request. Copy it. Then go to the Airdrop bot on the{' '}
          <a
            href="https://discord.com/channels/981694584474181654/1250916419370225926"
            target="_blank"
            rel="noreferrer"
          >
            #adb-airdrop channel
          </a>{' '}
          on Discord, click the “Forward Platform Rewards” button, and paste this message into the
          command line. Do this for every account you’d like to transfer your rewards from, then hit
          “Submit Claim to Mainnet Hub” before returning back to this flow.
        </Trans>
      </Text>
      <Stack
        style={{
          backgroundColor: theme.colors.background3[0],
          borderRadius: '12px',
          padding: '25px',
        }}
      >
        <Stack
          style={{
            gap: '0px',
            wordBreak: 'break-all',
          }}
        >
          <Text
            style={{
              fontFamily: 'monospace',
            }}
          >
            {discordMessage}
          </Text>
        </Stack>
      </Stack>
      <Stack style={{ flexDirection: 'row' }}>
        <RootButton
          leftSection={isCopied ? <IconCheck size="16px" /> : <IconCopy size="16px" />}
          onClick={() => {
            staticCopy(discordMessage, classes, theme, callbackCopyMessage(t))
          }}
          secondary
          style1={false}
          expand
        >
          {isCopied ? t('buttons.copied', 'Copied') : t('buttons.copy', 'Copy')}
        </RootButton>
        <RootButton expand style1={false} onClick={handleGoToDiscord} disabled={!enableGoToDiscord}>
          {t('buttons.goToDiscord', 'Go To Discord')}
        </RootButton>
      </Stack>
    </Stack>
  )
}

interface ChangeAddressProps {
  enteredDiscord: boolean
  setEnteredDiscord: (value: boolean) => void
}

const ChangeAddress: React.FC<ChangeAddressProps> = ({ enteredDiscord, setEnteredDiscord }) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const { address } = useAuth()
  const [changeAddress, setChangeAddress] = useState<string>('')
  const [touched, setTouched] = useState<boolean>(false)
  // signature
  const { data: signature, error: errorSigning, status, signMessage } = useSignMessage()
  const isLoading = useMemo(() => status === 'pending', [status])
  const [rewards] = useSessionStorage<AirdropRecipient[]>(`addressRewards-${address}`, [])

  const msg = useMemo(
    () =>
      JSON.stringify({
        rewards: rewards.map(reward => ({
          airdropId: reward.AirdropID,
          nodeId: reward.NodeID,
        })),
        original_address: address,
        new_address: changeAddress,
      }),
    [address, changeAddress, rewards]
  )

  const msgHash = useMemo(() => hashMessage(msg), [msg])

  const discordMessage = useMemo(
    () => JSON.stringify({ message: msg, msg_hash: msgHash, signature }),
    [msg, msgHash, signature]
  )

  const rejectedSigning = errorSigning?.message.includes('User rejected the request')

  const handleChangingAddress = useCallback((e: any) => {
    setTouched(true)
    setChangeAddress(e.target.value)
  }, [])
  return (
    <>
      {!signature && (
        <Stack style={{ gap: '20px' }}>
          <Text style={{ color: theme.colors.title[0], fontSize: '18px' }}>
            {t(
              'components.airdrop.modal.didYouSignRequest',
              'Sign request to transfer airdrop reward to a new address?'
            )}
          </Text>
          <Title order={3}>
            {t('components.airdrop.modal.inputAddress', 'Input Mainnet Address')}
          </Title>
          <SpecialInputTile
            value={changeAddress}
            placeholder="Input your address here"
            errorMessage={!isAddress(changeAddress) && touched ? `Please type a valid address` : ''}
            onChange={handleChangingAddress}
            tip="Enter the Mainnet alphanumeric address you’d like to forward to."
          />
          <Text style={{ color: theme.colors.title[0], fontSize: '18px' }}>
            {t(
              'components.airdrop.modal.agree',
              'By clicking sign, you agree to forward all the platform rewards from this account to the above Mainnet address. You attest that this address is your address, or an address you trust. '
            )}
          </Text>
          <Text style={{ color: theme.colors.title[0], fontSize: '18px' }}>
            {t(
              'components.airdrop.modal.scam',
              'Be wary of scams asking you to input their address into this field for rewards eligibility or bonuses. The LAMINA1 core team will never ask you to input an address here that is not your own.'
            )}
          </Text>
          {isLoading ? (
            <Loading />
          ) : (
            <Stack style={{ flexDirection: 'row' }}>
              <RootButton
                expand
                style1={false}
                onClick={() => signMessage({ message: msg })}
                disabled={!isAddress(changeAddress)}
              >
                {t('buttons.sign', 'Sign')}
              </RootButton>
            </Stack>
          )}
          {rejectedSigning && (
            <ErrorMessage
              title={t('components.airdrop.modal.rejected', 'Rejected signing the message')}
              message={t(
                'components.airdrop.modal.rejectedMsg',
                'You have rejected the signing request. Please sign the message to proceed.'
              )}
            />
          )}
          {errorSigning && !rejectedSigning && (
            <ErrorMessage
              message={t('components.airdrop.modal.signError', 'Error signing')}
              details={errorSigning.message}
            />
          )}
        </Stack>
      )}

      {signature &&
        (enteredDiscord ? (
          <ConfirmedChange setEnteredDiscord={setEnteredDiscord} />
        ) : (
          <SignedChangeAddress
            discordMessage={discordMessage}
            setEnteredDiscord={setEnteredDiscord}
          />
        ))}
    </>
  )
}

export default ChangeAddress
